import styled from "@emotion/styled";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Fade,
  Skeleton,
  Typography,
} from "@mui/material";
import { useGenerateThreadSuggestionPrompt } from "@queries/chat/useGenerateThreadSuggestionPrompt";
import type { GetThreadSuggestedPromptsResponse } from "@shared/apis/sidekick/generated";
import { useUIStore } from "@store/ui.store";
import { useCallback, useState } from "react";

const MAX_PROMPT_EXCEPTIONS = 9;
const SUGGESTION_COUNT = 3;

const LayoutRoot = styled.div`
  align-self: flex-start;
  width: 100%;
  max-width: 800px;
  min-width: 0;
`;

const Container = styled.div`
  margin: 1rem 0 2.5rem 0;
  position: relative;
  min-width: 0;
  max-width: 100%;
  &[aria-disabled="true"] {
    pointer-events: none;
    user-select: none;
    * {
      pointer-events: none;
      user-select: none;
    }
  }

  ${(p) => p.theme.media.tabletDown} {
    margin: 0rem 0 2rem 0;
  }
`;

const SuggestionsCard = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
  width: ${(p) => (p.$fullWidth ? "100%" : "max-content")};
  min-width: min(800px, 100%);
  max-width: 100%;
  padding: 0.75rem 0.75rem 0.75rem;
  border-radius: 0.5rem;
  background-color: ${(p) => p.theme.colors.background5};
`;

const HeaderLabel = styled(Typography)`
  text-transform: uppercase;
  color: ${(p) => p.theme.text.colors.brand};
`;

const SuggestionsAccordion = styled(Accordion)`
  width: 100%;
  box-shadow: none;
`;

const SuggestionsAccordionSummary = styled(AccordionSummary)`
  padding: 0;
  background: ${(p) => p.theme.colors.background5};
  margin: 0;
  min-height: 1.5rem;
  justify-content: flex-start;

  .MuiAccordionSummary-content {
    flex: 0 0 auto;
    width: auto;
    padding: 0;
    margin: 0;
    min-height: 1.5rem;
    display: flex;
    align-items: center;
    overflow: hidden;
  }

  .MuiAccordionSummary-expandIconWrapper {
    margin-left: 0.25rem;
    color: ${(p) => p.theme.text.colors.brand};
  }
`;

const SuggestionsAccordionDetails = styled(AccordionDetails)`
  padding: 0;
  padding-top: 0.5rem;
  background: ${(p) => p.theme.colors.background5};
`;

const SuggestionsStack = styled.div<{ $stretch?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  width: ${(p) => (p.$stretch ? "100%" : "auto")};
  min-width: 0;
`;

const SuggestionPill = styled.button`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  max-width: 100%;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  font-family: inherit;
  text-align: left;
  border: 1px solid ${(p) => p.theme.colors.border2};
  margin: 0;
  background-color: ${(p) => p.theme.colors.background};
  cursor: pointer;
  transition: background-color 0.15s ease;
  color: ${(p) => p.theme.button.text.colors.text};

  &:hover:not(:disabled) {
    background-color: ${(p) => p.theme.colors.background5};
  }

  &:disabled {
    opacity: 0.7;
    cursor: default;
  }
`;

const PillLabel = styled(Typography)`
  color: ${(p) => p.theme.button.text.colors.text};
  flex: 1;
  min-width: 0;
  overflow-wrap: break-word;
  word-break: break-word;
`;

const PillRowSkeletonSlot = styled.div`
  align-self: stretch;
  width: 100%;
  box-sizing: border-box;
`;

const RefreshRow = styled(Button)`
  color: ${(p) => p.theme.text.colors.brand};
  font-size: 0.75rem;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  align-self: flex-start;
  margin: 0.5rem 0 0 0;
  padding: 0.5rem 0;

  &:disabled {
    opacity: 0.6;
    background-color: transparent;
    cursor: default;
  }
`;

type Props = {
  threadId: string | undefined;
  isBlocked: boolean;
  onThreadSuggestionGenerated?: () => void;
  onShowSuggestionsClick?: () => void;
  onHideSuggestionsClick?: () => void;
  onRefreshSuggestionsClick: () => void;
  onThreadSuggestionClick: (suggestion: { prompt: string }) => void;
};

const PILL_SKELETON_HEIGHT = 44;

const FullWidthPillSkeleton = () => (
  <PillRowSkeletonSlot aria-busy="true" aria-label="Loading suggestion">
    <Skeleton
      variant="rounded"
      animation="wave"
      height={PILL_SKELETON_HEIGHT}
      sx={{ width: "100%", borderRadius: "0.5rem" }}
    />
  </PillRowSkeletonSlot>
);

function normalizeThree(suggested: string[]): string[] {
  return suggested.map((s) => s.trim()).slice(0, SUGGESTION_COUNT);
}

export const ProactiveMessageSuggestions = (props: Props) => {
  const {
    threadId,
    isBlocked,
    onThreadSuggestionGenerated,
    onShowSuggestionsClick,
    onHideSuggestionsClick,
    onThreadSuggestionClick,
    onRefreshSuggestionsClick,
  } = props;

  const panelExpanded = useUIStore((s) => s.isChatSuggestionsExpanded);
  const setSuggestionsExpanded = useUIStore(
    (s) => s.setChatSuggestionsExpanded,
  );

  const [promptExceptions, setPromptExceptions] = useState<string[]>([]);

  const baseEnabled = !!threadId && !isBlocked;

  const handleQuerySuccess = useCallback(
    (_data: GetThreadSuggestedPromptsResponse) => {
      setTimeout(() => {
        onThreadSuggestionGenerated?.();
      }, 200);
    },
    [onThreadSuggestionGenerated],
  );

  const suggestionsQuery = useGenerateThreadSuggestionPrompt({
    threadId: threadId ?? "",
    exceptions: promptExceptions,
    enabled: baseEnabled && panelExpanded,
    onSuccess: handleQuerySuccess,
  });

  const suggestedPrompts = normalizeThree(
    suggestionsQuery.data?.suggestedPrompts ?? [],
  );

  const isLoading = suggestionsQuery.isFetching;
  const showSkeletons = isLoading;
  const cardUsesFullWidth = showSkeletons;

  const handleRefreshClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!threadId || isBlocked || isLoading || !panelExpanded) return;
      onRefreshSuggestionsClick();
      const current = suggestedPrompts.filter((p) => p.length > 0);
      setPromptExceptions((prev) =>
        [...prev, ...current].slice(-MAX_PROMPT_EXCEPTIONS),
      );
    },
    [
      threadId,
      isBlocked,
      isLoading,
      panelExpanded,
      suggestedPrompts,
      onRefreshSuggestionsClick,
    ],
  );

  const handlePromptSuggestionClick = useCallback(
    (prompt: string) => {
      if (isBlocked) return;
      onThreadSuggestionClick({ prompt });
    },
    [isBlocked, onThreadSuggestionClick],
  );

  const isReadyToShowSuggestions = !isBlocked;

  return (
    <LayoutRoot>
      <Fade in={isReadyToShowSuggestions}>
        <Container aria-disabled={isBlocked || isLoading}>
          <SuggestionsCard $fullWidth={cardUsesFullWidth}>
            <SuggestionsAccordion
              expanded={panelExpanded}
              onChange={(_e, nextExpanded) => {
                if (isBlocked) return;
                setSuggestionsExpanded(nextExpanded);
                if (nextExpanded) {
                  onShowSuggestionsClick?.();
                } else {
                  onHideSuggestionsClick?.();
                }
              }}
              disableGutters
            >
              <SuggestionsAccordionSummary
                expandIcon={<ArrowDropDownIcon sx={{ color: "inherit" }} />}
                aria-controls="proactive-suggestions-menu"
                id="proactive-suggestions-toggle"
              >
                <HeaderLabel variant="button">Suggestions</HeaderLabel>
              </SuggestionsAccordionSummary>

              <SuggestionsAccordionDetails id="proactive-suggestions-menu">
                <SuggestionsStack $stretch={showSkeletons}>
                  {showSkeletons
                    ? Array.from({ length: SUGGESTION_COUNT }, (_, i) => (
                        <FullWidthPillSkeleton key={`skel-${i}`} />
                      ))
                    : suggestedPrompts.map((prompt, index) => (
                        <SuggestionPill
                          key={`${prompt.slice(0, 48)}-${index}`}
                          type="button"
                          disabled={isBlocked}
                          onClick={() => handlePromptSuggestionClick(prompt)}
                        >
                          <AutoAwesomeIcon
                            sx={{
                              fontSize: 16,
                              color: "inherit",
                              flexShrink: 0,
                            }}
                          />
                          <PillLabel variant="subtitle2">{prompt}</PillLabel>
                        </SuggestionPill>
                      ))}
                </SuggestionsStack>

                <RefreshRow
                  variant="text"
                  onClick={handleRefreshClick}
                  disabled={!threadId || isBlocked || isLoading || !panelExpanded}
                >
                  {isLoading ? (
                    <Skeleton
                      variant="circular"
                      animation="wave"
                      width={16}
                      height={16}
                      sx={{ flexShrink: 0 }}
                    />
                  ) : (
                    <RefreshIcon sx={{ fontSize: 16, color: "inherit" }} />
                  )}
                  Refresh suggestions
                </RefreshRow>
              </SuggestionsAccordionDetails>
            </SuggestionsAccordion>
          </SuggestionsCard>
        </Container>
      </Fade>
    </LayoutRoot>
  );
};
